import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import PhotoCarousel from '@alltrails/cards/components/PhotoCarousel';
import TrailMetadataStats from '@alltrails/cards/components/TrailMetadataStats';
import useTrailPhotos from '@alltrails/cards/hooks/useTrailPhotos';
import Typography from '@alltrails/core/components/Typography';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import PhotoSize from '@alltrails/types/PhotoSize';
import styles from './styles.module.scss';

type TrailSegmentCardProps = {
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  ranking: number;
  trail: TrailSearchResult;
};

const TrailSegmentCard = ({ onMouseEnter, onMouseLeave, ranking, trail }: TrailSegmentCardProps) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();
  const { photos, loadPhotos } = useTrailPhotos(trail.ID, Boolean(trail.has_profile_photo), PhotoSize.Large, '320x320', 5);

  const onClick = useCallback(() => {
    logTrailCardClicked({
      card_location: 'multi day route page',
      detailed_card_location: 'multi day route page segment list',
      trail_id: String(trail.ID)
    });
  }, [trail.ID]);

  return (
    <a
      className={styles.trailCard}
      href={wrapUrlSafe(`/${trail.slug}`, languageRegionCode)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      target="_blank"
    >
      <div className={styles.photoCarousel}>
        <PhotoCarousel arrowStyle="extra-subtle" aspectRatio="square" loadPhotos={loadPhotos} photos={photos} uniqueId={trail.id} />
      </div>
      <Typography className={styles.title} component="h3" variant={isMobile ? 'heading200' : 'heading300'}>
        {intl.formatMessage({ defaultMessage: '#{order} - {trailName}' }, { order: ranking, trailName: trail.name })}
      </Typography>
      <TrailMetadataStats
        avgRating={trail.avg_rating}
        compact
        difficulty={trail.difficulty_rating ? Number(trail.difficulty_rating) : undefined}
        durationText={trail.estimated_time_to_complete_text}
        durationMinutes={trail.duration_minutes}
        lengthMeters={trail.length}
        lengthText={trail.length_text}
        reviewCount={trail.num_reviews}
        size={isMobile ? 'sm' : 'md'}
      />
      <Typography className={styles.description} variant="text100">
        {trail.description}
      </Typography>
    </a>
  );
};

export default TrailSegmentCard;
